import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { useLang } from "../context/LanguageContext";
import { T, pick } from "../lib/translations";
import { ALL_CITIES } from "../lib/relatedContent";
import { getPosts, getBootPosts, prefetchPost } from "../lib/blogCache";
import { trackServerEvent } from "../lib/analytics";
import { GUIDES, GUIDE_ORDER } from "../lib/guides";
import { ArrowRight, BookOpen, MapPin, Phone, Search, X } from "lucide-react";

const FALLBACK_COVER =
  "https://images.unsplash.com/photo-1589994965851-a8f479c573a9?w=1200";

const decodeHtml = (str) => {
  if (!str) return "";
  const el = document.createElement("textarea");
  el.innerHTML = str;
  return el.value;
};

const stripTags = (html) => {
  if (!html) return "";
  const el = document.createElement("div");
  el.innerHTML = html;
  return (el.textContent || el.innerText || "").trim();
};

// Friendly filter labels for category chips
const PRETTY = {
  "BAIL": "Bail",
  "Offense-Specific Bail Guides": "Offence Guides",
  "Judicial Reform": "Judicial Reform",
  "Process & Types": "Process",
  "bail-process": "Bail Process",
  "what-it-costs": "Costs",
  timeline: "Timelines",
  "legal-terms": "Glossary",
  // New post taxonomy
  "offence-guides": "Offence Guides",
  "arrest-rights": "Arrest & Rights",
  "costs-and-scams": "Costs & Scams",
  perspectives: "Perspectives",
};
const pretty = (cat) => {
  const label = PRETTY[cat] || cat || "Article";
  return decodeHtml(label); // clean &amp; entities in imported WP category names
};

// Category pages live at /blog/category/<slug> — this slugify must stay in sync
// with the one in scripts/prerender.js
const slugify = (s) =>
  s.toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

// Quick offence filters — urgent one-tap searches for common case types
const QUICK_OFFENCES = [
  { label: "498A", query: "498A" },
  { label: "NDPS", query: "NDPS" },
  { label: "Financial Fraud", query: "fraud" },
  { label: "Dowry Death", query: "dowry" },
  { label: "POCSO", query: "POCSO" },
];

export default function BlogPage() {
  const { lang } = useLang();
  const isHi = lang === "hi";
  const { catSlug } = useParams();
  const navigate = useNavigate();
  const [posts, setPosts] = useState(() => getBootPosts() || []);
  const [loading, setLoading] = useState(() => !getBootPosts());
  const [query, setQuery] = useState("");

  useEffect(() => {
    getPosts().then((j) => {
      setPosts(j);
      setLoading(false);
    });
  }, []);

  // Search-term analytics — debounced 1.5s, only meaningful queries
  useEffect(() => {
    const q = query.trim();
    if (q.length < 3) return;
    const t = setTimeout(() => {
      trackServerEvent("search", { placement: "knowledge-hub", detail: q });
    }, 1500);
    return () => clearTimeout(t);
  }, [query]);

  // Unified resource items: guides (static, always available) + blog posts
  const guideItems = useMemo(
    () =>
      GUIDE_ORDER.map((slug) => {
        const g = GUIDES[slug];
        return {
          kind: "guide",
          id: `guide-${slug}`,
          slug,
          title: pick(g.title, lang),
          catLabel: pretty(slug),
          excerpt: pick(g.metaDescription || g.h1Sub || {}, lang),
          to: `/guide/${slug}`,
          search: `${pick(g.title, lang)} ${pick(g.metaDescription || {}, lang)} ${slug}`,
        };
      }),
    [lang]
  );

  const postItems = useMemo(
    () =>
      posts.map((p) => ({
        kind: "post",
        id: p.id,
        slug: p.slug,
        title: decodeHtml(p.title),
        catLabel: pretty(p.category || "Article"),
        excerpt: stripTags(p.excerpt).slice(0, 180),
        cover: p.cover,
        date: p.date,
        read_time: p.read_time,
        to: `/blog/${p.slug}`,
        search: `${decodeHtml(p.title)} ${stripTags(p.excerpt)} ${p.category || ""} ${p.slug}`,
      })),
    [posts]
  );

  const allItems = useMemo(() => [...guideItems, ...postItems], [guideItems, postItems]);

  // Category chips: "All" + guide slugs + every post category (deduped)
  const chips = useMemo(() => {
    const set = new Map();
    allItems.forEach((it) => set.set(it.catLabel, it.catLabel));
    return ["all", ...[...set.values()]];
  }, [allItems]);

  // Category comes from the URL (/blog/category/:catSlug) so filtered views are
  // real, crawlable pages. An unknown slug matches nothing instead of showing all.
  const urlCat = useMemo(
    () => (catSlug ? chips.find((c) => c !== "all" && slugify(c) === catSlug) || null : null),
    [chips, catSlug]
  );
  const activeCat = catSlug ? urlCat || catSlug : "all";

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allItems.filter((it) => {
      const matchesCat = activeCat === "all" || it.catLabel === activeCat;
      const matchesQuery = !q || it.search.toLowerCase().includes(q);
      return matchesCat && matchesQuery;
    });
  }, [allItems, activeCat, query]);

  return (
    <>
      <SEO
        title={
          urlCat && !isHi
            ? `${urlCat} — Bail Guides & Articles`
            : isHi
              ? "Rihai संसाधन — ज़मानत गाइड्स और लेख एक जगह"
              : "Rihai Resources — Bail Guides & Articles in One Place"
        }
        description={
          isHi
            ? "ज़मानत प्रक्रिया, लागत, समय और अपराध-विशिष्ट गाइड — खोजें और श्रेणी से फ़िल्टर करें।"
            : "Search every bail guide and article — process, costs, timeline, and offence-specific help. Filter by topic."
        }
        path={catSlug ? `/blog/category/${catSlug}` : "/blog"}
      />
      <Nav />
      <main className="pt-32 pb-20">
        <div className="container-x">
          <div className="max-w-2xl mb-10">
            <div className="overline mb-5">{pick(T.blog.overline, lang)}</div>
            <h1
              className="font-serif-display serif-textured text-5xl sm:text-6xl font-light leading-tight mb-4"
              data-testid="blog-h1"
            >
              {isHi ? "सभी संसाधन — एक जगह।" : "Every resource, one place."}
            </h1>
            <p className="text-lg text-[#4A4640] leading-relaxed">
              {isHi
                ? "गाइड्स और लेख — खोजें या विषय से चुनें।"
                : "Guides and articles — search, or filter by topic."}
            </p>
          </div>

          {/* Search bar */}
          <div className="relative max-w-xl mb-6" data-testid="blog-search-wrap">
            <Search
              size={18}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-[#736F66]"
            />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={
                isHi
                  ? "खोजें — NDPS, 498A, लागत, समय…"
                  : "Search — NDPS, 498A, cost, timeline…"
              }
              className="w-full pl-11 pr-10 py-3.5 rounded-full border border-[#D8CDBA] bg-white focus:outline-none focus:ring-2 focus:ring-[#2C4C3B] text-[#1A1A1A]"
              data-testid="blog-search-input"
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#736F66] hover:text-[#1A1A1A]"
                data-testid="blog-search-clear"
                aria-label="Clear search"
              >
                <X size={16} />
              </button>
            )}
          </div>

          {/* Quick offence filters — one-tap urgent searches */}
          <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="offence-quick-filters">
            <span className="text-xs uppercase tracking-widest text-[#736F66] mr-1">
              {isHi ? "अपराध:" : "Offence:"}
            </span>
            {QUICK_OFFENCES.map((o) => (
              <button
                key={o.label}
                onClick={() => { setQuery(o.query); if (catSlug) navigate("/blog"); }}
                className={`rounded-full px-3.5 py-1.5 text-sm border transition-colors ${
                  query.trim().toLowerCase() === o.query.toLowerCase()
                    ? "bg-[#A83F32] text-white border-[#A83F32]"
                    : "border-[#A83F32]/40 text-[#A83F32] hover:bg-[#A83F32] hover:text-white"
                }`}
                data-testid={`offence-chip-${o.label.replace(/\s+/g, "-").toLowerCase()}`}
              >
                {o.label}
              </button>
            ))}
          </div>

          {/* Category chips */}
          <div className="flex flex-wrap gap-2 mb-12" data-testid="blog-chips">
            {chips.map((c) => (
              <button
                key={c}
                onClick={() => navigate(c === "all" ? "/blog" : `/blog/category/${slugify(c)}`)}
                className={`rounded-full px-4 py-2 text-sm border transition-colors ${
                  activeCat === c
                    ? "bg-[#2C4C3B] text-white border-[#2C4C3B]"
                    : "border-[#D8CDBA] text-[#4A4640] hover:border-[#2C4C3B] hover:text-[#2C4C3B]"
                }`}
                data-testid={`blog-chip-${c === "all" ? "all" : c.replace(/\s+/g, "-").toLowerCase()}`}
              >
                {c === "all" ? (isHi ? "सभी" : "All") : c}
              </button>
            ))}
          </div>

          {/* Urgency strip */}
          <div
            className="mb-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 rounded-2xl bg-[#EAE6DF] border border-[#D8CDBA] px-6 py-5"
            data-testid="blog-urgency-strip"
          >
            <div className="text-sm text-[#4A4640]">
              <span className="font-medium text-[#1A1A1A]">
                {isHi ? "अभी कोई हिरासत में है?" : "Someone in custody right now?"}
              </span>{" "}
              {isHi ? "लेख खत्म होने का इंतज़ार मत कीजिए।" : "Don't wait for the article to finish."}
            </div>
            <div className="flex gap-3">
              <a
                href={`tel:${T.phone.number}`}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#A83F32] text-[#A83F32] text-sm font-medium hover:bg-[#A83F32] hover:text-white transition"
                data-testid="blog-strip-call"
              >
                <Phone size={14} strokeWidth={2} /> {T.phone.display}
              </a>
              <Link to="/#lead" className="btn-primary text-sm py-2" data-testid="blog-strip-lead">
                {pick(T.nav.cta, lang)}
              </Link>
            </div>
          </div>

          {/* Unified grid */}
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="blog-loading">
              {[0, 1, 2, 3, 4, 5].map((s) => (
                <div key={s} className="animate-pulse">
                  <div className="rounded-3xl bg-[#EAE6DF] aspect-[4/3] mb-5" />
                  <div className="h-3 w-20 bg-[#EAE6DF] rounded mb-3" />
                  <div className="h-6 w-11/12 bg-[#EAE6DF] rounded mb-2" />
                  <div className="h-4 w-full bg-[#EAE6DF] rounded" />
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-[#736F66] py-10" data-testid="blog-empty">
              {isHi ? "इस खोज के लिए कुछ नहीं मिला।" : "Nothing matches this search."}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="blog-grid">
              {filtered.map((it) => (
                <Link
                  key={it.id}
                  to={it.to}
                  onMouseEnter={it.kind === "post" ? () => prefetchPost(it.slug) : undefined}
                  className="group"
                  data-testid={`resource-card-${it.id}`}
                >
                  <article>
                    {it.kind === "post" ? (
                      <div className="rounded-3xl overflow-hidden aspect-[4/3] mb-5 bg-[#EAE6DF] relative">
                        <img
                          src={it.cover || FALLBACK_COVER}
                          alt={it.title}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                      </div>
                    ) : (
                      <div className="rounded-3xl aspect-[4/3] mb-5 bg-[#2C4C3B]/5 border border-[#2C4C3B]/15 flex items-center justify-center relative">
                        <BookOpen size={44} className="text-[#2C4C3B]/40" strokeWidth={1.25} />
                        <span className="absolute top-4 left-4 text-[10px] font-semibold uppercase tracking-widest bg-[#2C4C3B] text-white px-2.5 py-1 rounded-full">
                          {isHi ? "गाइड" : "Guide"}
                        </span>
                      </div>
                    )}
                    <div className="overline mb-2">{it.catLabel}</div>
                    <h3 className="font-serif-display text-2xl font-medium leading-snug text-[#1A1A1A] mb-2 group-hover:text-[#2C4C3B] transition-colors">
                      {it.title}
                    </h3>
                    <p className="text-sm text-[#4A4640] leading-relaxed line-clamp-3">
                      {it.excerpt}
                    </p>
                    <div className="mt-4 flex items-center justify-between">
                      {it.kind === "post" ? (
                        <div className="text-xs text-[#736F66] flex items-center gap-3">
                          <span>{it.date}</span>·<span>{it.read_time}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-[#736F66]">{isHi ? "संपूर्ण गाइड" : "Complete guide"}</span>
                      )}
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-[#2C4C3B] group-hover:gap-2 transition-all">
                        {pick(T.blog.read, lang)} <ArrowRight size={14} />
                      </span>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          )}

          {/* Verified lawyers by city */}
          <div className="mt-20 pt-12 border-t border-[#D8CDBA]" data-testid="blog-cities-strip">
            <div className="overline mb-4">
              {isHi ? "शहर के अनुसार सत्यापित वकील" : "Verified bail lawyers by city"}
            </div>
            <div className="flex flex-wrap gap-2">
              {ALL_CITIES.map((ct) => (
                <Link
                  key={ct.slug}
                  to={`/${ct.slug}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#D8CDBA] px-4 py-2 text-sm text-[#4A4640] hover:border-[#2C4C3B] hover:text-[#2C4C3B] transition-colors"
                  data-testid={`blog-city-${ct.slug}`}
                >
                  <MapPin size={12} strokeWidth={2} /> {isHi ? ct.cityHindi : ct.city}
                </Link>
              ))}
            </div>
          </div>

          {/* Bottom CTA banner */}
          <div
            className="mt-20 rounded-3xl bg-[#2C4C3B] text-white p-10 md:p-14 relative overflow-hidden"
            data-testid="blog-bottom-cta"
          >
            <div className="grain" />
            <div className="tricolor-band tricolor-band-thick mb-8 max-w-[120px]" />
            <h3 className="font-serif-display text-3xl md:text-4xl font-light leading-tight mb-4 relative max-w-2xl">
              {isHi ? "पढ़ना शुरुआत है। कॉल एक क़दम है।" : "Reading is a start. A call is a step."}
            </h3>
            <p className="text-[#D8CDBA] max-w-lg mb-8 relative">
              {isHi
                ? "जब आप तैयार हों, एक सत्यापित ज़मानत वकील एक कॉल दूर है।"
                : "When you're ready, a verified bail lawyer is one ring away."}
            </p>
            <div className="flex flex-wrap gap-3 relative">
              <Link
                to="/#lead"
                className="btn-primary bg-white text-[#2C4C3B] hover:bg-[#EAE6DF]"
                data-testid="blog-bottom-cta-lead"
              >
                {pick(T.hero.ctaPrimary, lang)} <ArrowRight size={16} />
              </Link>
              <a
                href={`tel:${T.phone.number}`}
                className="btn-secondary border-white text-white hover:bg-white hover:text-[#2C4C3B]"
                data-testid="blog-bottom-cta-call"
              >
                <Phone size={16} strokeWidth={2} /> {T.phone.display}
              </a>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
